import type { PaymentRowDto } from '../api/paymentsApi';

export interface PaymentGroup {
  groupName: string;
  isGang: boolean;
  payments: PaymentRowDto[];
  totalAmount: number;
}

const GANG_PREFIX = 'Gang - ';

/**
 * Groups payments by pot reference into gang groups and a single solo group.
 * Gang groups are sorted alphabetically, solo group always last.
 */
export function groupPaymentsByPot(payments: PaymentRowDto[]): PaymentGroup[] {
  const gangMap = new Map<string, PaymentRowDto[]>();
  const soloPayments: PaymentRowDto[] = [];

  for (const payment of payments) {
    if (payment.potReference.startsWith(GANG_PREFIX)) {
      const gangName = payment.potReference.slice(GANG_PREFIX.length);
      const existing = gangMap.get(gangName);
      if (existing) {
        existing.push(payment);
      } else {
        gangMap.set(gangName, [payment]);
      }
    } else {
      soloPayments.push(payment);
    }
  }

  const gangGroups: PaymentGroup[] = [...gangMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([gangName, groupPayments]) => ({
      groupName: gangName,
      isGang: true,
      payments: groupPayments,
      totalAmount: groupPayments.reduce((sum, p) => sum + p.amountDue, 0),
    }));

  if (soloPayments.length > 0) {
    gangGroups.push({
      groupName: 'Solo Pots',
      isGang: false,
      payments: soloPayments,
      totalAmount: soloPayments.reduce((sum, p) => sum + p.amountDue, 0),
    });
  }

  return gangGroups;
}

/**
 * Groups payments by individual pot reference.
 * Each unique potReference becomes its own collapsible group, sorted alphabetically.
 */
export function groupPaymentsByPotReference(
  payments: PaymentRowDto[]
): PaymentGroup[] {
  const potMap = new Map<string, PaymentRowDto[]>();

  for (const p of payments) {
    const existing = potMap.get(p.potReference);
    if (existing) existing.push(p);
    else potMap.set(p.potReference, [p]);
  }

  return [...potMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([ref, groupPayments]) => ({
      groupName: ref,
      isGang: ref.startsWith(GANG_PREFIX),
      payments: groupPayments,
      totalAmount: groupPayments.reduce((sum, p) => sum + p.amountDue, 0),
    }));
}
